// FlattenedJsonResult — generic MCP/external tool result: flattened text by
// default, with a toggle to pretty-printed JSON.

import { useMemo, useState } from 'react';
import { flattenValue, parseJsonPayload } from './parse-tool-payload';
import type { ToolResultRendererProps } from './types';
import { CodeBlock, ProseBlock } from './shared';

const LINE_CAP = 20;

export function FlattenedJsonResult({ text }: ToolResultRendererProps) {
  const [mode, setMode] = useState<'formatted' | 'json'>('formatted');
  const [showAll, setShowAll] = useState(false);

  const parsed = useMemo(() => parseJsonPayload(text), [text]);

  if (parsed == null) {
    return (
      <div data-testid="tool-result-flattened">
        <ProseBlock>{text || 'No content'}</ProseBlock>
      </div>
    );
  }

  const lines = flattenValue(parsed);
  const visible = showAll || lines.length <= LINE_CAP ? lines : lines.slice(0, LINE_CAP);
  const jsonPretty = JSON.stringify(parsed, null, 2);

  return (
    <div data-testid="tool-result-flattened" className="space-y-1.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="tool-result-json-toggle"
          className="text-caption text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setMode((m) => (m === 'formatted' ? 'json' : 'formatted'))}
        >
          {mode === 'formatted' ? 'View as JSON' : 'View formatted'}
        </button>
      </div>

      {mode === 'json' ? (
        <CodeBlock text={jsonPretty} testId="tool-result-json" />
      ) : (
        <>
          <div data-testid="tool-result-formatted" className="space-y-0.5 text-body">
            {visible.map((line, i) => (
              <div
                key={i}
                className="break-words text-foreground/90"
                style={{ paddingLeft: `${line.depth * 12}px` }}
              >
                {line.text}
              </div>
            ))}
          </div>
          {lines.length > LINE_CAP && (
            <button
              type="button"
              className="text-caption text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAll((s) => !s)}
            >
              {showAll ? 'Show less' : `Show ${lines.length - LINE_CAP} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
